import type { components } from '@trackercop/contracts';

type ComponentName = components['schemas']['ComponentName'];

export interface ComponentDescription {
  /** One-sentence definition of the dependency. */
  what: string;
  /** Concrete app surfaces that depend on this component. */
  dependsOn: string;
  /** Healthy threshold in plain English. */
  healthy: string;
  /** Warning threshold. */
  warning: string;
  /** Critical threshold. */
  critical: string;
  /** First-action operator hint when the tile is degraded. */
  remediation: string;
}

export const COMPONENT_DESCRIPTIONS: Record<ComponentName, ComponentDescription> = {
  d1: {
    what: 'D1 is the relational SQLite store on Cloudflare. The probe times a `SELECT 1` round-trip on the bound database.',
    dependsOn:
      'Every persisted entity reads or writes through D1 — sites, crawls, pages, payload records, GTM tags, finding reviews, alert subscriptions, audit logs.',
    healthy: 'Round-trip under 1500 ms.',
    warning:
      'Round-trip > 1500 ms. The threshold deliberately accommodates regional cold-start latency, so a single warning often clears on the next probe.',
    critical: 'Probe threw — D1 binding is unreachable or the database is rejecting connections.',
    remediation:
      'Sustained warnings: check status.cloudflare.com for D1 incidents. Critical: confirm the DB binding name in `wrangler.jsonc` matches the deployed environment and the database has not been deleted.',
  },
  kv: {
    what: 'Workers KV is the eventually-consistent key-value store. The probe writes a heartbeat and reads it back on every cycle.',
    dependsOn:
      'JWKS public-key cache (Cloudflare Access JWT verification), 30-second analytics-response cache, observability snapshot blobs (`obs:health:<name>`), heartbeat keys for cron / browser-run / sendgrid recency probes.',
    healthy: 'Round-trip write+read under 600 ms.',
    warning:
      'Round-trip > 600 ms. Often a regional KV propagation delay; concerning only if it persists across multiple cycles.',
    critical: 'Probe threw — KV binding unreachable or the heartbeat read returned an unexpected value.',
    remediation:
      'Sustained slow KV usually means a Cloudflare-side region issue. Auth and observability degrade gracefully on transient failures, but a hard outage will start dropping JWT verifications within an hour.',
  },
  r2: {
    what: 'R2 is the S3-compatible object store. The probe runs a write-free `.head()` against a sentinel key.',
    dependsOn: 'Raw payload blob storage (the unredacted captured request bodies) and scan harness artifacts.',
    healthy: 'Head request under 400 ms.',
    warning: 'Head request > 400 ms — usually a transient regional issue.',
    critical: 'Probe threw — R2 binding unreachable.',
    remediation:
      'A degraded R2 stops new payload writes from persisting; scans continue to run but raw-payload audit reads will fail. Check status.cloudflare.com.',
  },
  'tracker-radar': {
    what: 'DuckDuckGo Tracker Radar is the bundled tracker categorisation corpus, shipped as a static asset at build time.',
    dependsOn:
      'Tracker categorisation in scan results (analytics / advertising / social / cdn labels on observed scripts).',
    healthy: 'Bundled snapshot less than 30 days old.',
    warning: 'Snapshot ≥ 30 days old — categorisation is drifting from the current tracker landscape.',
    critical: 'Snapshot ≥ 90 days old — categorisation is significantly stale.',
    remediation:
      'Re-run `node packages/common/scripts/fetch-tracker-radar.mjs` and ship a new build. The fetch is wired to `prebuild`/`pretest`; a long-stale snapshot usually means the upstream fetch is failing silently.',
  },
  'workers-api': {
    what: 'The public HTTP surface of the Workers package. The probe pulls error rate from Cloudflare GraphQL Analytics over the last 5 minutes.',
    dependsOn: 'Every public API endpoint — admin, dashboard reads, scan dispatch, alert subscription mutations.',
    healthy: 'Error rate ≤ 1 % over the last 5 minutes.',
    warning: 'Error rate > 1 % — investigate logs for the offending route.',
    critical: 'Error rate > 5 % — a deployed change is throwing or the runtime is misconfigured.',
    remediation:
      '`wrangler tail` for the worker, filter by 5xx; the offending route name is in the log line. If error rate spikes on deploy, roll back.',
  },
  'scan-queue': {
    what: 'Cloudflare Queue carrying enqueued scan jobs. The probe pulls depth and consumer error ratio from GraphQL Analytics.',
    dependsOn: 'Per-site scan dispatch — the daily cron and admin "run scan" actions both enqueue here.',
    healthy: 'Depth < 500 and consumer error ratio < 5 %.',
    warning: 'Depth ≥ 500 OR error ratio ≥ 5 %. Cron-driven enqueue can briefly burst above 500 — concerning if sustained.',
    critical: 'Depth ≥ 2000 OR error ratio ≥ 20 % — the consumer cannot keep up or is failing.',
    remediation:
      'Check the `ScanOrchestratorDO` alarm path and Browser Run capacity. A growing queue with healthy consumer logs usually means Browser Run sessions are timing out.',
  },
  'alert-queue': {
    what: 'Cloudflare Queue carrying outbound email-alert jobs. Lower-volume than the scan queue; thresholds are tighter.',
    dependsOn: 'Outbound email alerts — breach notifications for finding reviews, scan completions.',
    healthy: 'Depth < 50 and error ratio < 10 %.',
    warning: 'Depth ≥ 50 OR error ratio ≥ 10 %. SendGrid throttling or a wedged consumer.',
    critical: 'Depth ≥ 200 OR error ratio ≥ 30 % — alerts are not being delivered.',
    remediation:
      'Cross-reference with the `sendgrid` tile. If SendGrid is healthy, the consumer is the culprit — check `alert-queue-consumer` logs.',
  },
  dlq: {
    what: 'Dead-letter queue. Messages land here after exhausting retries on the scan or alert queues.',
    dependsOn: 'Scan + alert recovery path — DLQ messages need manual inspection or replay.',
    healthy: 'Depth = 0. Any DLQ message is a real failure to investigate.',
    warning: 'Depth ≥ 1.',
    critical: 'Depth ≥ 10 — a class of message is consistently failing.',
    remediation:
      'Inspect DLQ contents via the Cloudflare dashboard. The silent-killer `dlq_growing` check also fires here, so an alert email should already be in flight.',
  },
  sendgrid: {
    what: 'SendGrid is the transactional email provider for breach alerts. The probe blends two signals — a `GET /v3/user/profile` auth check, and KV heartbeats the dispatcher writes on every real send attempt and 202 success.',
    dependsOn:
      'Outbound email delivery for finding-review breach alerts, scan completion summaries, and platform SLO breaches.',
    healthy: 'Profile returns 2xx under 3 s AND either a successful 202 within the last hour or no recent send attempts (a quiet env is healthy).',
    warning: 'Profile returns a non-auth 5xx — usually a SendGrid-side incident.',
    critical:
      'Profile returns 401/403 (token revoked or insufficient scope), the profile times out after 3 s, OR the dispatcher attempted a send within the last hour but no 202 followed (mail is rejected post-auth). Cross-reference the alert-queue and dlq tiles.',
    remediation:
      'Auth failure: rotate `SENDGRID_API_KEY`. "Attempts but no success": check SendGrid Activity for bounce / block / domain-auth issues; the API key has `mail.send` scope but a sender-domain authentication lapse (DKIM/SPF) blocks delivery without a 4xx on the API.',
  },
  jwks: {
    what: 'JWKS is the public-key set Cloudflare Access publishes for JWT verification. The probe reads `obs:jwks:last-refresh-ts` from KV — the verifier writes that key after each successful refresh.',
    dependsOn: 'Cloudflare Access JWT verification on every authenticated request.',
    healthy: 'Last refresh < 2 hours ago.',
    warning: 'Last refresh ≥ 2 hours and < 24 hours ago.',
    critical: 'Last refresh ≥ 24 hours ago — token verification will start failing as keys roll.',
    remediation:
      'A stale heartbeat usually means no authenticated traffic in the window (the verifier only refreshes on demand). If real traffic is hitting the worker, check the `CloudflareAccessIdpVerifier` logs for `jose` errors.',
  },
  cron: {
    what: 'The scheduled trigger that runs the 5-minute observability rollup cron. The probe reads `obs:cron:last-run-ts` from KV — the cron writes that key on every successful tick.',
    dependsOn:
      'Hourly D1 metric snapshots, health-probe execution, silent-killer breach detection, KV health-blob writes (`obs:health:<name>`).',
    healthy: 'Last run < 15 minutes ago.',
    warning: 'Last run ≥ 15 minutes ago — one or two missed ticks.',
    critical: 'Last run ≥ 60 minutes ago — the scheduled trigger has stopped firing.',
    remediation:
      'Confirm the cron trigger is registered in `wrangler.jsonc` and the cron handler is exported from `src/index.ts`. Cloudflare cron triggers are silently disabled when the worker fails to deploy; check the latest deploy.',
  },
  'durable-object': {
    what: 'Durable Object health is sampled from one representative `ScanOrchestratorDO` instance via RPC; the probe grades on `lastAlarmAt` recency.',
    dependsOn: 'Scan orchestration — one DO per `${tenantId}:${siteId}`, driving page-by-page scan execution.',
    healthy: 'Last alarm fired < 30 minutes ago.',
    warning: 'Last alarm fired ≥ 30 minutes ago.',
    critical: 'Last alarm fired ≥ 2 hours ago — the alarm path is broken or the DO is wedged.',
    remediation:
      '`unknown` is normal in a quiet environment with no representative DO configured. If a real instance is wedged, inspect the DO `scan_state` table for a stuck `running` row.',
  },
  'browser-run': {
    what: 'Cloudflare Browser Run hosts the Puppeteer sessions used by the scanner. The probe reads `obs:browser:last-success-ts` from KV — the executor writes that key after each completed page.',
    dependsOn: 'Puppeteer-based scan execution — page navigation, request interception, consent emulation, payload classification.',
    healthy: 'Last successful page < 1 hour ago.',
    warning: 'Last successful page ≥ 1 hour ago.',
    critical: 'Last successful page ≥ 6 hours ago.',
    remediation:
      '`unknown` is normal until the first scan completes a page. Trigger a manual scan from any site to validate. Persistent staleness with active scans means Browser Run sessions are crashing — check `BrowserRunExecutor` logs.',
  },
};
